import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCheck, Loader2, Paperclip, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import api from '../../api/client';
import { useAuth } from '../../context/AuthContext';

/**
 * HelpFormModal — Phase 48.
 * -----------------------------------------------------------------
 * Renders a dynamic help-center form fetched by key. All widget
 * types listed in help_center.py's ALLOWED_FIELD_TYPES are rendered
 * inline; unknown types are silently skipped (never crashes).
 *
 * Submits multipart to POST /api/help/tickets:
 *   - form_key, payload_json (JSON), name/email (for anon)
 *   - `files[]` — up to 3 attachments, 5MB each.
 * The parent controls open/close via `open` + `onOpenChange`.
 */
const HelpFormModal = ({ formKey, open, onOpenChange }) => {
  const { t, i18n } = useTranslation('helpCenter');
  const { user } = useAuth();
  const lang = i18n.language?.startsWith('en') ? 'en' : 'it';

  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [values, setValues] = useState({});
  const [attachments, setAttachments] = useState([]);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null); // {reference}
  // Anti-bot honeypot — must stay empty.
  const [honeypot, setHoneypot] = useState('');

  // Load form definition every time modal opens.
  useEffect(() => {
    if (!open || !formKey) return;
    setLoading(true);
    setError(null);
    setSuccess(null);
    setValues({});
    setAttachments([]);
    setErrors({});
    api.get(`/help/forms/${formKey}`)
      .then((res) => setForm(res.data))
      .catch((_e) => setError('not_found'))
      .finally(() => setLoading(false));
  }, [open, formKey]);

  // Pre-fill contact info from logged-in user.
  useEffect(() => {
    if (user) {
      setName(user.full_name || user.username || '');
      setEmail(user.email || '');
    } else {
      setName('');
      setEmail('');
    }
  }, [user, open]);

  const localize = (obj) => {
    if (!obj) return '';
    if (typeof obj === 'string') return obj;
    return obj[lang] || obj.it || obj.en || Object.values(obj)[0] || '';
  };

  const validate = () => {
    const next = {};
    if (!user) {
      if (!name.trim()) next.name = t('form_modal.required');
      if (!email.trim() || !/^\S+@\S+\.\S+$/.test(email)) next.email = t('form_modal.invalid_email');
    }
    (form?.fields || []).forEach((f) => {
      const raw = values[f.key];
      if (f.type === 'file') return;
      if (f.required && (raw === undefined || raw === '' || raw === null)) {
        next[f.key] = t('form_modal.required');
        return;
      }
      if (typeof raw === 'string' && f.validation) {
        if (f.validation.min && raw.length < f.validation.min) {
          next[f.key] = t('form_modal.min_chars', { n: f.validation.min });
        }
        if (f.validation.max && raw.length > f.validation.max) {
          next[f.key] = t('form_modal.max_chars', { n: f.validation.max });
        }
      }
    });
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (submitting) return;
    if (!validate()) return;
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('form_key', form.key);
      fd.append('payload_json', JSON.stringify(values));
      fd.append('name', name);
      fd.append('email', email);
      fd.append('locale', lang);
      fd.append('website', honeypot);
      attachments.slice(0, 3).forEach((f) => fd.append('files', f, f.name));
      const res = await api.post('/help/tickets', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setSuccess({ reference: res.data.reference });
    } catch (_e) {
      setErrors((prev) => ({ ...prev, _root: t('form_modal.required') }));
    } finally {
      setSubmitting(false);
    }
  };

  const onChangeField = (key) => (val) => {
    setValues((prev) => ({ ...prev, [key]: val }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const onPickFiles = (e) => {
    const files = Array.from(e.target.files || []).slice(0, 3 - attachments.length);
    const kept = files.filter((f) => f.size <= 5 * 1024 * 1024);
    setAttachments((prev) => [...prev, ...kept].slice(0, 3));
    e.target.value = '';
  };

  const removeAttachment = (idx) => {
    setAttachments((prev) => prev.filter((_, i) => i !== idx));
  };

  const title = useMemo(() => (form ? localize(form.title) : ''), [form, lang]);
  const description = useMemo(() => (form ? localize(form.description) : ''), [form, lang]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg border-amber-500/25 bg-neutral-950"
        data-testid="help-form-modal"
      >
        <DialogHeader>
          <DialogTitle className="text-lg" data-testid="help-form-title">
            {loading ? t('form_modal.loading') : (error ? t('form_modal.not_found') : title)}
          </DialogTitle>
          {!loading && !error && description && (
            <DialogDescription className="text-[13px] text-muted-foreground">
              {description}
            </DialogDescription>
          )}
        </DialogHeader>

        {loading && (
          <div className="flex justify-center py-8 text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t('form_modal.loading')}
          </div>
        )}

        {!loading && success && (
          <div className="py-6 text-center" data-testid="help-form-success">
            <div className="mx-auto mb-4 inline-flex h-12 w-12 items-center justify-center rounded-full bg-amber-500/15 text-amber-200">
              <CheckCheck className="h-6 w-6" />
            </div>
            <h3 className="text-base font-semibold">{t('form_modal.success_title')}</h3>
            <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-muted-foreground">
              {t('form_modal.success_body', { reference: success.reference })}
            </p>
            <Button onClick={() => onOpenChange(false)} className="mt-6 gold-gradient text-black" data-testid="help-form-close-success">
              {t('form_modal.close')}
            </Button>
          </div>
        )}

        {!loading && !error && !success && form && (
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="help-form">
            {/* Honeypot */}
            <div className="hidden" aria-hidden="true">
              <input
                tabIndex={-1}
                autoComplete="off"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
              />
            </div>

            {!user && (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <Label className="text-[12px]">{t('form_modal.name_label')}</Label>
                  <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={t('form_modal.name_placeholder')}
                    className="mt-1"
                    data-testid="help-form-field-name"
                  />
                  {errors.name && <p className="mt-1 text-[11px] text-red-400">{errors.name}</p>}
                </div>
                <div>
                  <Label className="text-[12px]">{t('form_modal.email_label')}</Label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t('form_modal.email_placeholder')}
                    className="mt-1"
                    data-testid="help-form-field-email"
                  />
                  {errors.email && <p className="mt-1 text-[11px] text-red-400">{errors.email}</p>}
                </div>
              </div>
            )}

            {(form.fields || []).map((f) => (
              <FieldRenderer
                key={f.key}
                field={f}
                lang={lang}
                value={values[f.key]}
                onChange={onChangeField(f.key)}
                error={errors[f.key]}
                localize={localize}
                onPickFiles={onPickFiles}
                attachments={attachments}
                removeAttachment={removeAttachment}
                addLabel={t('form_modal.add_attachment')}
                removeLabel={t('form_modal.remove_attachment')}
                attHint={t('form_modal.attachments_hint')}
                selectPlaceholder={t('form_modal.select_placeholder')}
              />
            ))}

            <DialogFooter className="mt-2 gap-2">
              <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} data-testid="help-form-cancel">
                {t('form_modal.close')}
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="gold-gradient text-black"
                data-testid="help-form-submit"
              >
                {submitting ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />{t('form_modal.submitting')}</>) : t('form_modal.submit')}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

/**
 * Field renderer — switch on `field.type`. Keeps the modal readable.
 */
const FieldRenderer = ({
  field, value, onChange, error, lang, localize, onPickFiles, attachments,
  removeAttachment, addLabel, removeLabel, attHint, selectPlaceholder,
}) => {
  const label = localize(field.label);
  const placeholder = field.placeholder ? localize(field.placeholder) : '';
  const help = field.help_text ? localize(field.help_text) : '';
  const testId = `help-form-field-${field.key}`;

  const commonWrapper = (child) => (
    <div>
      <Label className="text-[12px]">
        {label}{field.required && <span className="ml-0.5 text-amber-300">*</span>}
      </Label>
      {child}
      {help && !error && <p className="mt-1 text-[11px] text-muted-foreground">{help}</p>}
      {error && <p className="mt-1 text-[11px] text-red-400">{error}</p>}
    </div>
  );

  switch (field.type) {
    case 'text':
    case 'email':
    case 'phone':
      return commonWrapper(
        <Input
          type={field.type === 'email' ? 'email' : (field.type === 'phone' ? 'tel' : 'text')}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="mt-1"
          data-testid={testId}
        />
      );
    case 'number':
      return commonWrapper(
        <Input
          type="number"
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="mt-1"
          data-testid={testId}
        />
      );
    case 'date':
      return commonWrapper(
        <Input
          type="date"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          className="mt-1"
          data-testid={testId}
        />
      );
    case 'textarea':
      return commonWrapper(
        <Textarea
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          rows={4}
          className="mt-1"
          data-testid={testId}
        />
      );
    case 'select':
      return commonWrapper(
        <Select value={value || ''} onValueChange={(v) => onChange(v)}>
          <SelectTrigger className="mt-1" data-testid={testId}>
            <SelectValue placeholder={placeholder || selectPlaceholder} />
          </SelectTrigger>
          <SelectContent>
            {(field.options || []).map((o) => (
              <SelectItem key={o.value} value={o.value} data-testid={`${testId}-opt-${o.value}`}>
                {localize(o.label)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    case 'radio':
      return commonWrapper(
        <div className="mt-2 space-y-1.5" data-testid={testId}>
          {(field.options || []).map((o) => (
            <label key={o.value} className="flex cursor-pointer items-center gap-2 text-[13px]">
              <input
                type="radio"
                name={field.key}
                value={o.value}
                checked={value === o.value}
                onChange={() => onChange(o.value)}
                className="accent-amber-400"
                data-testid={`${testId}-opt-${o.value}`}
              />
              <span>{localize(o.label)}</span>
            </label>
          ))}
        </div>
      );
    case 'checkbox':
      return (
        <div>
          <label className="flex cursor-pointer items-start gap-2 text-[13px]">
            <input
              type="checkbox"
              checked={!!value}
              onChange={(e) => onChange(e.target.checked)}
              className="mt-1 accent-amber-400"
              data-testid={testId}
            />
            <span>{label}{field.required && <span className="ml-0.5 text-amber-300">*</span>}</span>
          </label>
          {help && !error && <p className="ml-6 mt-1 text-[11px] text-muted-foreground">{help}</p>}
          {error && <p className="ml-6 mt-1 text-[11px] text-red-400">{error}</p>}
        </div>
      );
    case 'file':
      return (
        <div>
          <Label className="text-[12px]">{label}</Label>
          <div className="mt-1 space-y-1.5">
            {attachments.map((f, i) => (
              <div key={i} className="flex items-center justify-between rounded-md border border-white/10 bg-white/[0.03] px-2 py-1 text-[12px]" data-testid={`help-form-attachment-${i}`}>
                <span className="truncate">
                  <Paperclip className="mr-1 inline h-3 w-3" />
                  {f.name} · {Math.round(f.size / 1024)}KB
                </span>
                <button type="button" onClick={() => removeAttachment(i)} className="text-red-300" aria-label={removeLabel}>
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
            {attachments.length < 3 && (
              <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-dashed border-white/15 px-2 py-1.5 text-[12px] text-muted-foreground hover:border-amber-400/40 hover:text-foreground">
                <Paperclip className="h-3 w-3" />
                {addLabel}
                <input type="file" multiple hidden onChange={onPickFiles} accept="image/*,application/pdf" data-testid={testId} />
              </label>
            )}
          </div>
          <p className="mt-1 text-[11px] text-muted-foreground">{attHint}</p>
        </div>
      );
    default:
      return null;
  }
};

export default HelpFormModal;
